import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LuCheck, LuLayers, LuPlus } from 'react-icons/lu';
import { apiFetch, apiJson } from '../apiClient';
import { FACTORY_BRICK_TYPE_TEMPLATES } from '../constants/factoryBrickTypeTemplates';
import BrickSizeField from '../components/BrickSizeField';
import './FactoryBrickTypesSetupPage.css';

const emptyCustom = () => ({
  name: '',
  code: '',
  sizeLabel: '',
  defaultSellingPrice: '',
});

export default function FactoryBrickTypesSetupPage({ onNavigate }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedTemplates, setSelectedTemplates] = useState(() => new Set(['solid-6', 'hollow-6']));
  const [custom, setCustom] = useState(emptyCustom);

  const go = (key) => {
    if (typeof onNavigate === 'function') onNavigate(key);
  };

  const loadTypes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiJson('/brick/types');
      setTypes(Array.isArray(data.types) ? data.types : []);
    } catch {
      setTypes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTypes();
  }, [loadTypes]);

  const existingCodes = useMemo(() => {
    const set = new Set();
    for (const t of types) {
      if (t.code) set.add(String(t.code).toLowerCase());
      set.add(String(t.name || '').trim().toLowerCase());
    }
    return set;
  }, [types]);

  const templatesToAdd = useMemo(
    () =>
      FACTORY_BRICK_TYPE_TEMPLATES.filter((tpl) => {
        if (!selectedTemplates.has(tpl.id)) return false;
        const codeKey = String(tpl.code || '').toLowerCase();
        const nameKey = String(tpl.name || '').toLowerCase();
        return !existingCodes.has(codeKey) && !existingCodes.has(nameKey);
      }),
    [selectedTemplates, existingCodes]
  );

  const toggleTemplate = (id) => {
    setSelectedTemplates((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const createType = async (payload) => {
    const res = await apiFetch('/brick/types', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(body.message || 'Could not save brick type.');
    }
    return body.type;
  };

  const handleAddTemplates = async () => {
    if (templatesToAdd.length === 0) {
      setError('Chagua aina mpya au zote tayari zipo.');
      return;
    }
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      for (const tpl of templatesToAdd) {
        await createType({
          name: tpl.name,
          code: tpl.code,
          sizeLabel: tpl.sizeLabel,
          unit: tpl.unit,
          stockOnHand: 0,
        });
      }
      setSuccess(`Imeongeza aina ${templatesToAdd.length} za matofali.`);
      setSelectedTemplates(new Set());
      await loadTypes();
    } catch (err) {
      setError(err.message || 'Imeshindwa kuongeza templates.');
    } finally {
      setBusy(false);
    }
  };

  const handleAddCustom = async (e) => {
    e.preventDefault();
    const name = custom.name.trim();
    if (!name) {
      setError('Andika jina la aina ya tofali.');
      return;
    }
    const normName = name.toLowerCase();
    const normCode = custom.code.trim().toLowerCase();
    const normSize = custom.sizeLabel.trim().toLowerCase();
    const duplicate = types.find((t) => {
      if (normName && String(t.name || '').trim().toLowerCase() === normName) return true;
      if (normCode && String(t.code || '').trim().toLowerCase() === normCode) return true;
      if (normSize && String(t.sizeLabel || '').trim().toLowerCase() === normSize) return true;
      return false;
    });
    if (duplicate) {
      setError(
        `This brick type already exists in your catalog ("${duplicate.name}"). Please review the existing entry or edit it instead of adding a duplicate.`
      );
      return;
    }
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await createType({
        name,
        code: custom.code.trim() || null,
        sizeLabel: custom.sizeLabel.trim() || null,
        defaultSellingPrice: custom.defaultSellingPrice ? Number(custom.defaultSellingPrice) : null,
        stockOnHand: 0,
      });
      setSuccess(`"${name}" imeongezwa.`);
      setCustom(emptyCustom());
      await loadTypes();
    } catch (err) {
      setError(err.message || 'Imeshindwa kuongeza aina.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="fbrick-setup page-bleed">
      <header className="fbrick-setup__hero">
        <div className="fbrick-setup__hero-icon" aria-hidden>
          <LuLayers size={22} />
        </div>
        <div>
          <h1 className="fbrick-setup__title">Aina za matofali mnazozalisha</h1>
          <p className="fbrick-setup__sub">Weka aina za matofali mnazozalisha.</p>
        </div>
      </header>

      {loading ? <p className="fbrick-setup__muted">Loading…</p> : null}

      {!loading && types.length > 0 ? (
        <div className="fbrick-setup__existing">
          <h2 className="fbrick-setup__section-title">
            <LuCheck size={18} aria-hidden /> Tayari umeweka aina {types.length}
          </h2>
          <ul className="fbrick-setup__list">
            {types.map((t) => (
              <li key={t.id}>
                <strong>{t.name}</strong>
                {t.sizeLabel ? <span> — {t.sizeLabel}</span> : null}
              </li>
            ))}
          </ul>
          <div className="fbrick-setup__existing-actions">
            <button type="button" className="fbrick-setup__btn fbrick-setup__btn--primary" onClick={() => go('brick-production')}>
              Endelea — production batch
            </button>
            <button type="button" className="fbrick-setup__btn" onClick={() => go('brick-catalog')}>
              Hariri catalog kamili
            </button>
          </div>
        </div>
      ) : null}

      <section className="fbrick-setup__panel">
        <h2 className="fbrick-setup__section-title">Chagua kutoka templates</h2>
        <div className="fbrick-setup__templates">
          {FACTORY_BRICK_TYPE_TEMPLATES.map((tpl) => {
            const codeKey = String(tpl.code || '').toLowerCase();
            const nameKey = String(tpl.name || '').toLowerCase();
            const exists = existingCodes.has(codeKey) || existingCodes.has(nameKey);
            const checked = selectedTemplates.has(tpl.id);
            return (
              <label
                key={tpl.id}
                className={`fbrick-setup__tpl${exists ? ' fbrick-setup__tpl--exists' : ''}${checked ? ' fbrick-setup__tpl--on' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={exists || busy}
                  onChange={() => toggleTemplate(tpl.id)}
                />
                <span className="fbrick-setup__tpl-name">{tpl.name}</span>
                <span className="fbrick-setup__tpl-meta">{tpl.sizeLabel}</span>
                {exists ? <span className="fbrick-setup__tpl-badge">Added</span> : null}
              </label>
            );
          })}
        </div>
        <button
          type="button"
          className="fbrick-setup__btn fbrick-setup__btn--primary"
          disabled={busy || templatesToAdd.length === 0}
          onClick={() => void handleAddTemplates()}
        >
          Ongeza templates ({templatesToAdd.length})
        </button>
      </section>

      <section className="fbrick-setup__panel">
        <h2 className="fbrick-setup__section-title">
          <LuPlus size={18} aria-hidden /> Aina maalum
        </h2>
        <form className="fbrick-setup__form" onSubmit={handleAddCustom}>
          <label>
            Jina *
            <input
              value={custom.name}
              onChange={(e) => setCustom((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Tofali maalum 8 inch"
              disabled={busy}
              required
            />
          </label>
          <label>
            Code (SKU)
            <input
              value={custom.code}
              onChange={(e) => setCustom((f) => ({ ...f, code: e.target.value }))}
              placeholder="Optional"
              disabled={busy}
            />
          </label>
          <label>
            Ukubwa
            <BrickSizeField
              value={custom.sizeLabel}
              onChange={(sizeLabel) => setCustom((f) => ({ ...f, sizeLabel }))}
              disabled={busy}
            />
          </label>
          <label>
            Bei ya kuuza (TZS)
            <input
              type="number"
              min="0"
              value={custom.defaultSellingPrice}
              onChange={(e) => setCustom((f) => ({ ...f, defaultSellingPrice: e.target.value }))}
              placeholder="Optional"
              disabled={busy}
            />
          </label>
          <button type="submit" className="fbrick-setup__btn fbrick-setup__btn--primary" disabled={busy}>
            Ongeza aina
          </button>
        </form>
      </section>

      {error ? (
        <p className="fbrick-setup__error" role="alert">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className="fbrick-setup__success" role="status">
          {success}
        </p>
      ) : null}
    </section>
  );
}
